// ─── World ────────────────────────────────────────────────────────────────────
// Use the actual browser viewport so the game fills the full window.
// Evaluated once at module load — refresh to pick up a window resize.
export const WORLD_WIDTH  = window.innerWidth;
export const WORLD_HEIGHT = window.innerHeight;

// ─── Entity sizes ─────────────────────────────────────────────────────────────
export const PLAYER_SIZE = 75;
export const ENEMY_SIZE  = 25;
export const ELITE_SIZE  = 30; // Elite enemy (2× HP, 2× damage)

// Rocks are clipped into an organic blob by CSS border-radius (~38-50% on each
// corner). The actual visible rock shape is roughly 72% of the full square.
// This scale is applied to both the collision size and its centred offset so
// the physics AABB matches what the player sees.
export const ROCK_COLLISION_SCALE = 0.72;

// ─── Movement ─────────────────────────────────────────────────────────────────
export const PLAYER_SPEED = 1.5; // reduced by 50%
export const ENEMY_SPEED  = 1.5;

// ─── Spawning ─────────────────────────────────────────────────────────────────
export const ENEMY_SPAWN_INTERVAL = 2000; // ms between spawn waves

// ─── Boss ─────────────────────────────────────────────────────────────────────
export const BOSS_SIZE            = 90;   // 60 px × 1.5 = 90 px
export const BOSS_HP              = 30;
export const BOSS_SPAWN_INTERVAL  = 30000; // ms between boss spawns
export const BOSS_COUNTDOWN_SEC   = 3;     // seconds of warning before spawn

// ─── Tier Boss ────────────────────────────────────────────────────────────────
export const TIER_BOSS_SIZE         = 150; // 100 px × 1.5 = 150 px — oversized goblin king
export const TIER_BOSS_WARNING_SEC  = 3;   // seconds of warning before spawn
export const TIER_BOSS_SWING_RANGE  = 150; // px — melee swing attack reach
export const TIER_BOSS_SWING_CD     = 4000; // ms between swing attacks

// ─── Attack ───────────────────────────────────────────────────────────────────
export const ATTACK_RADIUS       = 60;   // base radius in px (scaled by rangeMult)
export const MAX_ATTACK_RADIUS   = 200;  // hard cap on effective attack radius (px)
export const ATTACK_DURATION     = 400;  // ms the hitbox stays active
export const ATTACK_COOLDOWN     = 1000; // ms between attacks (scaled by cdMult)
export const MIN_ATTACK_COOLDOWN = 250;  // hard floor on effective cooldown (ms)

// ─── Dash ─────────────────────────────────────────────────────────────────────
export const DASH_DISTANCE = 200;  // total px moved during a dash
export const DASH_DURATION = 180;  // ms the dash lasts (player is invincible + invisible)
export const DASH_COOLDOWN = 2500; // ms between dashes

// ─── Block ────────────────────────────────────────────────────────────────────
export const BLOCK_COOLDOWN  = 5000;  // ms between blocks
export const BLOCK_PUSH_PX   = 50;   // px enemies are pushed away
export const BLOCK_IMMUNE_MS = 2000; // ms of invincibility granted

// ─── HP Potion ────────────────────────────────────────────────────────────────
export const POTION_COOLDOWN = 10000; // ms between uses
export const POTION_HEAL_PCT = 0.10;  // fraction of max HP restored per use
export const POTION_MAX      = 10;    // maximum potions the player can hold
export const POTION_COST     = 100;   // gold cost to craft one potion

// ─── Player health ────────────────────────────────────────────────────────────
export const ENEMY_DAMAGE = 10; // HP lost per enemy collision

// ─── Tier colours (cycle every 30 s) ─────────────────────────────────────────
export const TIER_COLORS = [
  { bg: '#e24a4a', border: '#f57a7a' }, // red
  { bg: '#e2844a', border: '#f5a87a' }, // orange
  { bg: '#9b4ae2', border: '#c07af5' }, // purple
  { bg: '#4ae2d4', border: '#7af5eb' }, // teal
  { bg: '#e2d44a', border: '#f5eb7a' }, // yellow
  { bg: '#e24aaa', border: '#f57acb' }, // pink
];

// ─── Skills pool ──────────────────────────────────────────────────────────────
export const SKILLS = [
  { id: 'speed',    label: '⚡ Swift Feet',  desc: 'Move faster (diminishing returns)',     apply: s => { s.speedMult     *= 1.2;  } },
  { id: 'range',    label: '🌊 Wide Sweep',  desc: '+20% attack range',   apply: s => { s.rangeMult     *= 1.2;  } },
  { id: 'cooldown', label: '🔄 Quick Hands', desc: '20% faster attack',   apply: s => { s.cdMult        *= 0.8;  } },
  { id: 'lifeup',   label: '❤️ Second Wind', desc: 'Restore 1 life',      apply: s => { s.lives = Math.min(s.maxLives ?? 3, s.lives + 1); } },
  { id: 'slow',     label: '🌀 Heavy Aura',  desc: 'Enemies 15% slower',  apply: s => { s.enemySlowMult *= 0.85; } },
  { id: 'xpboost',  label: '📚 Scholar',     desc: '+1 XP per kill',      apply: s => { s.xpPerKill    += 1;    } },
  { id: 'damage',   label: '💥 Power Strike', desc: '+1 attack damage',    apply: s => { s.attackDamage += 1;    } },
];

// ─── Initial game state factory ───────────────────────────────────────────────
export const createInitialState = () => ({
  player:          { x: WORLD_WIDTH / 2 - PLAYER_SIZE / 2, y: WORLD_HEIGHT / 2 - PLAYER_SIZE / 2 },
  velocity:        { x: 0, y: 0 },   // smooth acceleration vector
  mouse:           { x: 0, y: 0, down: false }, // left-click follow state
  // Player sprite direction & walk animation
  playerFacingAngle:      -Math.PI / 2, // radians; -π/2 = facing up (default)
  playerFrame:            0,            // 0 = KnightA, 1 = KnightB
  playerFrameLastToggle:  0,            // timestamp of last frame toggle
  playerIsMoving:         false,        // true while player has velocity > threshold
  enemies:         [],
  keys:            {},
  alive:           true,
  score:           0,
  nextEnemyId:     0,
  attack:          { active: false, startTime: 0, damageDealt: false },
  lastAttackTime:  -ATTACK_COOLDOWN,
  hp:              100,
  maxHp:           100,
  lastHitTime:     -99999,
  // XP / levelling
  xp:              0,
  level:           1,
  xpToNext:        5,
  // Skill multipliers
  speedMult:       1,
  rangeMult:       1,
  cdMult:          1,
  enemySlowMult:   1,
  xpPerKill:       1,
  attackDamage:    10,
  // Combat attributes
  armor:           0,     // flat damage reduction per hit
  fireRes:         0.0,   // fire resistance 0–75%
  lightningRes:    0.0,   // lightning resistance 0–75%
  resistance:      0.0,   // physical resistance 0–75% (from talents/items)
  magicFind:       0.0,   // magic find bonus — improves item drop rate + rarity
  critRate:        0.05,  // 5% base crit chance
  critDamage:      1.5,   // ×1.5 multiplier on crit hits
  dodgeChance:          0.0,   // % chance to fully dodge an enemy hit (0–75%)
  lastDodgeTime:       -99999, // timestamp of last successful dodge (drives blur visual)
  // Block modifiers (from talent tree)
  blockImmuneMsBonus:    0,   // ms added to BLOCK_IMMUNE_MS (block duration extension)
  blockCooldownReduction: 0,  // ms subtracted from BLOCK_COOLDOWN (min 100)
  // Boss
  nextBossSpawnAt: 0, // set to (timestamp + BOSS_SPAWN_INTERVAL) on first loop frame
  // Spells
  spellCooldowns:  {},   // { [spellId]: lastCastTimestamp }
  spellEffects:    [],   // [{ id, type, x, y, radius?, startTime, duration }]
  frozenUntil:     0,    // enemies can't move while performance.now() < frozenUntil
  shieldUntil:     0,    // player is invincible while performance.now() < shieldUntil
  slowTimeUntil:   0,    // enemies move at 30% speed while performance.now() < slowTimeUntil
  // Loot
  droppedItems:    [],   // items that dropped this run; collected on game over
  // Weapon attack shape (set by applyEquippedItems; unarmed = no weapon equipped)
  weaponType:      'unarmed',
  // Kill / gold tracking
  kills:           0,    // total enemy kills this run (melee + spells)
  goldThisRun:     0,    // gold earned this run; banked on game over
  goldFindBonus:   0,    // applied by applyPermanentUpgrades (e.g. 0.25 per goldFind level)
  // Floating texts (damage numbers, gold pickups)
  floatingTexts:   [],   // [{ id, x, y, text, color, startTime, duration }]
  // Tier system
  currentTier:     0,    // tracks the last announced tier index
  tierAnnouncement: null, // { tier, startTime } — cleared by GameWorld once expired
  lastMagicTier:   -1,   // tier at which magic selection was last offered (even tiers ≥ 2)
  // In-run spells — earned during a run via magic tier milestones; max 4 active
  runSpells:       [],   // [spellId, ...] — max 4
  runSpellLevels:  {},   // { spellId: level } — each spell starts at 1, upgrades to max 3

  // World modifier system — offered every 4 boss kills
  bossKills:       0,    // total boss kills this run (melee + spells)
  lastModifierAt:  -1,   // last threshold index (bossKills / 4) at which modifier was offered
  activeModifier:  null, // current modifier object from worldModifiers.js, or null

  // Tier Boss state
  tierBossAlive:        false, // true while a tier boss is alive on screen
  tierBossWarning:      null,  // { tier, startTime } — 3-second countdown; null when inactive
  lastTierBossAt:       -1,    // tier index at which last tier boss was triggered
  tierBossChestPending: false, // set true when tier boss dies (any source); cleared next frame

  // Rocks — solid scrolling obstacles
  rocks:           [],   // [{ id, x, y, size }] — scroll at background speed
  nextRockAt:      0,    // timestamp when next rock spawns (0 = not yet scheduled)
  nextRockId:      0,    // auto-incrementing rock ID

  // Ponds — terrain hazards scrolling with background
  ponds:           [],   // [{ id, x, y, size }] — scroll at background speed
  nextPondAt:      0,    // timestamp when next pond spawns (0 = not yet scheduled)
  nextPondId:      0,    // auto-incrementing pond ID
  pondSlowFactor:  1,    // 1 = not in pond; <1 = slowed (applied to player movement)
  pondLastDmgAt:   -99999, // last timestamp of lava-pond damage (1.5 s cooldown)

  // Trees — decorative scrolling foliage (no collision, purely visual)
  trees:           [],   // [{ id, x, y, size }] — scroll at background speed
  nextTreeAt:      0,    // timestamp when next tree spawns (0 = not yet scheduled)
  nextTreeId:      0,    // auto-incrementing tree ID

  // Chests — treasure chest entities (scroll with terrain; open for loot)
  chests:          [],   // [{ id, x, y, size }]
  nextChestId:     0,    // auto-incrementing chest ID

  // Ground drops — XP orbs and item drops lying on the world floor
  // { id, type: 'xp'|'item', x, y, size, item? }
  // Scroll with terrain; attracted to player within 60 px; collected on contact.
  groundDrops:     [],
  nextDropId:      0,

  // Projectiles — fired by ranged enemies toward the player (screen-space)
  projectiles:     [],   // [{ id, x, y, vx, vy, damage, damageType, spawnTime }]
  nextProjId:      0,    // auto-incrementing projectile ID

  // Background scroll — tracks total px scrolled (drives score)
  bgScrollTotal:   0,    // accumulated px; score = floor(bgScrollTotal)

  // Dash ability
  isDashing:       false,  // true while dash is in progress
  dashEndTime:     0,      // timestamp when current dash expires
  dashVelX:        0,      // normalised X direction of active dash
  dashVelY:        0,      // normalised Y direction of active dash
  lastDashTime:    -9999,  // timestamp of last dash (for cooldown)

  // Block ability
  lastBlockTime:   -9999,  // timestamp of last block (for cooldown)

  // HP Potions (count carried into the run; decremented on use)
  potionCount:     0,      // number of potions available (set from persistent state at run start)
  lastPotionTime:  -99999, // timestamp of last potion use (10 s cooldown)

  // ── Legendary item ability state ─────────────────────────────────────────
  damageMult:          1,     // Lich King's Signet sets to 2 (melee damage × multiplier)
  hitCooldown:         1500,  // ms between taking damage (Wraithshroud raises to 3000)
  soulDrainActive:     false, // Soulreaper's Edge
  soulDrainCounter:    0,
  voidPulseActive:     false, // Voidplate
  lichPactActive:      false, // Lich King's Signet
  bloodlustActive:     false, // Titan's War Gauntlets
  phantomStrideActive: false, // Phantom Stride Boots
  phantomStrideAt:     0,     // auto-blink fires at this timestamp (0 = not yet set)
  deathNovaActive:     false, // Amulet of Ruin
  solarFlareActive:    false, // Dawnbreaker Helm
  solarFlareReady:     false, // set true on level-up; consumed by next melee attack
  stormSurgeActive:    false, // Stormsurge Band

  // World-specific decorative assets (environment-themed decorations)
  worldAssets:         [],
  nextWorldAssetAt:    0,
  nextWorldAssetId:    0,

  // Environment — visual theme for background, ponds, rocks, trees
  // Randomized after each tier boss kill
  environment:         'forest', // 'forest'|'desert'|'snow'|'castle'|'water'|'graveyard'|'inferno'
});
